import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchProducts } from '../services/productApi';
import { useLanguage } from '../hooks/useLanguage';
import { useCurrency } from '../context/CurrencyContext';

const SearchPage = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const { getLocalizedPath } = useLanguage();
    const { formatPrice } = useCurrency();

    useEffect(() => {
        const loadResults = async () => {
            if (!query) {
                setProducts([]);
                return;
            }

            setLoading(true);
            try {
                const params = new URLSearchParams();
                params.append('search', query);
                params.append('limit', '50'); // Reasonable limit for search results

                const data = await fetchProducts(params);
                setProducts(data.data || []);
            } catch (error) {
                console.error("Search error:", error);
            } finally {
                setLoading(false);
            }
        };

        loadResults();
    }, [query]);

    return (
        <div className="min-h-screen pt-[120px] px-6 max-w-[1300px] mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">
                    {t('search.resultsFor', 'Search Results for')} "{query}"
                </h1>
                <p className="text-gray-500">
                    {loading
                        ? t('search.searching', 'Searching...')
                        : t('search.count', { count: products.length, defaultValue: `${products.length} items found` })
                    }
                </p>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} className="animate-pulse">
                            <div className="bg-gray-200 aspect-[3/4] rounded-lg mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    ))}
                </div>
            ) : products.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
                    {products.map((product) => (
                        <Link
                            key={product._id || product.id}
                            to={getLocalizedPath(`/product/${product._id || product.id}`)}
                            className="group block"
                        >
                            <div className="relative aspect-[3/4] mb-4 bg-gray-100 rounded-lg overflow-hidden">
                                <img
                                    src={product.images?.[0] || '/assets/product-image.webp'}
                                    alt={product.name}
                                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                                />

                                {/* Badges */}
                                {product.status === 'out_of_stock' && (
                                    <span className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded">
                                        Out of Stock
                                    </span>
                                )}
                                {product.stock <= product.minStock && product.stock > 0 && (
                                    <span className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
                                        Low Stock
                                    </span>
                                )}
                            </div>

                            <div>
                                <p className="text-sm text-gray-500 mb-1 capitalize">{product.brand}</p>
                                <h3 className="text-base font-medium text-gray-900 line-clamp-2 mb-1 group-hover:text-blue-600 transition-colors">
                                    {product.name}
                                </h3>
                                <div className="flex items-center space-x-2">
                                    <p className="text-base font-semibold text-gray-900">
                                        {formatPrice(product.price)}
                                    </p>
                                    {/* {product.comparePrice && (
                        <p className="text-sm text-gray-500 line-through">
                            {formatPrice(product.comparePrice)}
                        </p>
                    )} */}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            ) : (
                <div className="text-center py-20 bg-gray-50 rounded-lg">
                    <p className="text-xl text-gray-600 mb-4">No results found for "{query}"</p>
                    <p className="text-gray-500">Try checking your spelling or using different keywords.</p>
                    <Link to={getLocalizedPath('/')} className="mt-8 inline-block px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
                        Return Home
                    </Link>
                </div>
            )}
        </div>
    );
};

export default SearchPage;
